import { Badge, Card } from "@design";
import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { SkillSummary, ToolSummary } from "@/api";
import { Text } from "@/components/Text";

/**
 * The capability group sections for the agent Capabilities tab (extension
 * groups, ungrouped skills, knowledge). Split from AgentCapabilitiesTab.tsx
 * for the file-size convention.
 */

export function ExtensionGroups({
  skills,
  tools,
  skillSel,
  toolSel,
  canEdit,
  onToggleSkill,
  onToggleTool,
}: Readonly<{
  skills: SkillSummary[];
  tools: ToolSummary[];
  skillSel: Set<string>;
  toolSel: Set<string>;
  canEdit: boolean;
  onToggleSkill: (uuid: string) => void;
  onToggleTool: (uuid: string) => void;
}>) {
  // Group by parent extension.
  const groups = new Map<
    string,
    { name: string; color?: string | null; isEnabled: boolean; skills: SkillSummary[]; tools: ToolSummary[] }
  >();
  for (const skill of skills) {
    if (!skill.extension) continue;
    if (!groups.has(skill.extension.uuid))
      groups.set(skill.extension.uuid, {
        name: skill.extension.name,
        color: skill.extension.color,
        isEnabled: skill.extension.isEnabled,
        skills: [],
        tools: [],
      });
    groups.get(skill.extension.uuid)?.skills.push(skill);
  }
  for (const tool of tools) {
    if (!tool.extension) continue;
    if (!groups.has(tool.extension.uuid))
      groups.set(tool.extension.uuid, {
        name: tool.extension.name,
        color: tool.extension.color,
        isEnabled: tool.extension.isEnabled,
        skills: [],
        tools: [],
      });
    groups.get(tool.extension.uuid)?.tools.push(tool);
  }
  const sorted = [...groups.values()].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      {sorted.map((group) => (
        <CapabilityGroup
          key={group.name}
          title={group.name}
          disabled={!group.isEnabled}
          cards={[
            ...group.skills.map((skill) => ({
              uuid: skill.uuid,
              name: skill.name,
              description: skill.description,
              selected: skillSel.has(skill.uuid),
              onToggle: () => onToggleSkill(skill.uuid),
            })),
            ...group.tools.map((tool) => ({
              uuid: tool.uuid,
              name: tool.name,
              description: tool.description,
              selected: toolSel.has(tool.uuid),
              onToggle: () => onToggleTool(tool.uuid),
            })),
          ]}
          canEdit={canEdit}
        />
      ))}
    </>
  );
}

export function UngroupedSkills({
  skills,
  skillSel,
  canEdit,
  onToggle,
}: Readonly<{
  skills: SkillSummary[];
  skillSel: Set<string>;
  canEdit: boolean;
  onToggle: (uuid: string) => void;
}>) {
  const { t } = useTranslation("agents");
  if (skills.length === 0) return null;
  return (
    <CapabilityGroup
      title={t("capabilities.otherSkills")}
      cards={skills.map((skill) => ({
        uuid: skill.uuid,
        name: skill.name,
        description: skill.description,
        selected: skillSel.has(skill.uuid),
        onToggle: () => onToggle(skill.uuid),
      }))}
      canEdit={canEdit}
    />
  );
}

export function KnowledgeSection({
  knowledge,
  knowledgeSel,
  canEdit,
  onToggle,
}: Readonly<{
  knowledge: Array<{ uuid: string; name: string; description?: string | null; sourceType: string }>;
  knowledgeSel: Set<string>;
  canEdit: boolean;
  onToggle: (uuid: string) => void;
}>) {
  const { t } = useTranslation("agents");
  return (
    <CapabilityGroup
      title={t("capabilities.knowledge")}
      cards={knowledge.map((dc) => ({
        uuid: dc.uuid,
        name: dc.name,
        description: dc.description,
        badge: dc.sourceType,
        selected: knowledgeSel.has(dc.uuid),
        onToggle: () => onToggle(dc.uuid),
      }))}
      canEdit={canEdit}
    />
  );
}

/**
 * Connectors on the agent (phase 5, D21): all of a connector's tools at once. Attached ones I can't
 * use are listed with a note, since their tools won't work when I chat with the agent.
 */
export function ConnectorsSection({
  available,
  attached,
  selected,
  canEdit,
  onToggle,
}: Readonly<{
  available: Array<{ uuid: string; name: string; description?: string | null; integrationName?: string | null }>;
  attached: Array<{ uuid: string; name: string; description?: string | null; canUse: boolean }>;
  selected: Set<string>;
  canEdit: boolean;
  onToggle: (uuid: string) => void;
}>) {
  const { t } = useTranslation("agents");
  const mine = new Set(available.map((c) => c.uuid));
  const notMine = attached.filter((c) => !mine.has(c.uuid) || !c.canUse);
  return (
    <CapabilityGroup
      title={t("capabilities.connectors")}
      cards={[
        ...available
          .filter((c) => !notMine.some((n) => n.uuid === c.uuid))
          .map((c) => ({
            uuid: c.uuid,
            name: c.name,
            description: c.description,
            badge: c.integrationName ?? undefined,
            selected: selected.has(c.uuid),
            onToggle: () => onToggle(c.uuid),
          })),
        ...notMine.map((c) => ({
          uuid: c.uuid,
          name: c.name,
          description: t("capabilities.connectorNotYours"),
          selected: selected.has(c.uuid),
          onToggle: () => onToggle(c.uuid),
        })),
      ]}
      canEdit={canEdit}
    />
  );
}

/** A collapsible group of toggle-on-click capability cards under a heading. */
function CapabilityGroup({
  title,
  disabled,
  cards,
  canEdit,
}: Readonly<{
  title: string;
  disabled?: boolean;
  cards: Array<{
    uuid: string;
    name: string;
    description?: string | null;
    badge?: string;
    selected: boolean;
    onToggle: () => void;
  }>;
  canEdit: boolean;
}>) {
  const { t } = useTranslation("agents");
  // Disabled extensions are hidden (collapsed) by default — the list is long
  // and you can't enable their capabilities anyway, so they're noise.
  const [open, setOpen] = useState(!disabled);
  if (cards.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex cursor-pointer items-center gap-2 text-left btn-focus-ring"
      >
        <ChevronDown size={16} className={`text-content-tertiary transition-transform ${open ? "" : "-rotate-90"}`} />
        <Text variant="body" className="uppercase tracking-wide" tone="secondary">
          {title}
        </Text>
        {disabled && <Badge variant="danger">{t("capabilities.disabled")}</Badge>}
        <span className="text-content-tertiary [font:var(--type-caption)]">
          {t("capabilities.count", { count: cards.filter((c) => c.selected).length })}
        </span>
      </button>
      {open && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {cards.map((card) => (
            <Card
              key={card.uuid}
              onClick={canEdit && !disabled ? card.onToggle : undefined}
              interactive={canEdit && !disabled}
              className={card.selected ? "border-border-focus" : ""}
              aria-pressed={card.selected}
            >
              <div className="flex items-center gap-3">
                <div className="flex min-w-0 flex-1 flex-col">
                  <Text variant="body-sm" className="truncate">
                    {card.name}
                  </Text>
                  {card.description && (
                    <Text variant="caption" tone="tertiary" className="truncate">
                      {card.description}
                    </Text>
                  )}
                </div>
                {card.badge && (
                  <Badge variant="neutral" size="compact">
                    {card.badge}
                  </Badge>
                )}
                {card.selected && <Check size={16} className="shrink-0 text-content-primary" />}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
